import type { LocationQuery } from 'vue-router';
import { createFiltersFromQuery } from '~/stores/productFilterStore/helper/createFiltersFromQuery';
import { ALWAYS_ALLOWED_FILTER_KEYS } from '~/stores/productFilterStore/helper/knownFilterCodes';

/**
 * Whether an embedded product list section should mount immediately instead of
 * waiting for LazyLoad intersection — e.g. deep links with `#products` or
 * shared URLs that already carry known PLP pagination/sort/sku/category state.
 *
 * Facet-only query params (e.g. `?interface=USB3`) stay lazy: Magento facet
 * codes are not known until aggregations load, and allowing any non-tracking
 * key would eager-load on junk like `?foo=bar`.
 */
export function shouldEagerLoadProductListSection(
  hash: string,
  sectionId: string,
  query: LocationQuery
): boolean {
  const normalizedSectionId = sectionId.trim().toLowerCase();
  if (!normalizedSectionId) {
    return false;
  }

  const normalizedHash = hash.trim().toLowerCase();
  const sectionHash = `#${normalizedSectionId}`;
  if (
    normalizedHash === sectionHash ||
    normalizedHash.startsWith(`${sectionHash}?`) ||
    normalizedHash.startsWith(`${sectionHash}/`)
  ) {
    return true;
  }

  // ponytail: only static PLP keys; facet codes need aggregations first
  const filters = createFiltersFromQuery(query);
  return Object.keys(filters).some((key) =>
    ALWAYS_ALLOWED_FILTER_KEYS.has(key)
  );
}
